import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import OutlinedIconButton from '~components/Buttons/OutlinedIconButton';
import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { ReadOnlyField } from '~components/FormFields/ReadOnlyField';
import Menu, { MenuHandler } from '~components/Menu/Menu';
import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { CommandAlkonConnection } from '~hooks/useCommandAlkon/models';
import useCommandAlkon from '~hooks/useCommandAlkon/useCommandAlkon';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useCommandAlkonConnectionQuery, {
  getCommandAlkonConnectionQueryKey,
} from '~queries/useCommandAlkonConnectionQuery';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useSingleFlight from '~utils/hooks/useSingleFlight';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import CommandAlkonConnectForm from './CommandAlkonConnectForm';

const CommandAlkonManagement = observer(() => {
  const { userStore } = useStores();
  const featureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.commandAlkonIntegration,
  });
  const { canDeleteCommandAlkonIntegration, canManageCommandAlkonIntegration } =
    userStore.getPermissions();

  if (featureFlag.isAwaitingTreatment) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!featureFlag.isFeatureFlagEnabled || !canManageCommandAlkonIntegration) {
    return <Navigate to={routes.base} replace />;
  }

  return <CommandAlkonManagementPage canDelete={canDeleteCommandAlkonIntegration} />;
});

interface CommandAlkonManagementPageProps {
  canDelete: boolean;
}

const CommandAlkonManagementPage = observer(
  ({ canDelete }: CommandAlkonManagementPageProps) => {
    const { toasterStore, userStore } = useStores();
    const queryClient = useQueryClient();
    const { deleteConnection, testConnection } = useCommandAlkon();
    const companyId = getEffectiveUserCompanyId(userStore);

    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
    const [isReplacing, setIsReplacing] = useState(false);
    const [testResult, setTestResult] = useState<boolean | null>(null);
    const { isInFlight: isMutating, runSingleFlight } = useSingleFlight();
    const moreActionsMenuRef = useRef<MenuHandler>(null);
    const theme = useTheme();

    const { commandAlkonConnection, isError, isLoading } =
      useCommandAlkonConnectionQuery();

    const handleSaved = async (saved: CommandAlkonConnection) => {
      const queryKey = getCommandAlkonConnectionQueryKey(companyId);

      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, saved);
      setIsReplacing(false);
      setTestResult(null);
      toasterStore.push(
        alert(
          t(
            isReplacing
              ? 'administration.integration.command_alkon.toast.saved'
              : 'administration.integration.command_alkon.toast.connected',
          ),
          AlertTypes.success,
        ),
      );
    };

    const handleTestClick = () =>
      runSingleFlight(async () => {
        setTestResult(await testConnection(companyId));
      });

    const handleDisconnectConfirm = () =>
      runSingleFlight(async () => {
        if (!(await deleteConnection(companyId))) return;

        queryClient.setQueryData(getCommandAlkonConnectionQueryKey(companyId), null);
        setIsDisconnectDialogOpen(false);
        setIsReplacing(false);
        setTestResult(null);
        toasterStore.push(
          alert(
            t('administration.integration.command_alkon.toast.disconnected'),
            AlertTypes.success,
          ),
        );
      });

    const renderMoreActionsMenuItem = (label: string, onSelect: () => void) => (
      <MenuItem
        onClick={() => {
          moreActionsMenuRef.current?.onClose?.();
          onSelect();
        }}
      >
        <Typography variant="subtitle2" sx={{ color: theme.brandV2.colors.treadBlack }}>
          {label}
        </Typography>
      </MenuItem>
    );

    const renderBody = () => {
      if (isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        );
      }

      if (isError && !commandAlkonConnection) {
        return (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t('error_messages.command_alkon.failed_to_fetch_connection')}
          </Alert>
        );
      }

      if (!commandAlkonConnection) {
        return (
          <CommandAlkonConnectForm
            companyId={companyId}
            mode="connect"
            onSaved={handleSaved}
          />
        );
      }

      if (isReplacing) {
        return (
          <CommandAlkonConnectForm
            companyId={companyId}
            connection={commandAlkonConnection}
            mode="replace"
            onCancel={() => setIsReplacing(false)}
            onSaved={handleSaved}
          />
        );
      }

      return (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <ReadOnlyField
              label={t('administration.integration.command_alkon.form.entity_ref_label')}
              value={commandAlkonConnection.entityRef}
            />
            <ReadOnlyField
              label={t('administration.integration.command_alkon.form.client_id_label')}
              value={commandAlkonConnection.clientId}
            />
            <ReadOnlyField
              label={t(
                'administration.integration.command_alkon.form.client_secret_label',
              )}
              value={t('administration.integration.command_alkon.masked_secret', {
                lastFour: commandAlkonConnection.clientSecretLastFour,
                interpolation: { escapeValue: false },
              })}
            />
            <ReadOnlyField
              label={t(
                'administration.integration.command_alkon.form.api_scope_ref_label',
              )}
              value={commandAlkonConnection.apiScopeRef}
            />
            <ReadOnlyField
              label={t('administration.integration.command_alkon.form.api_key_label')}
              value={t('administration.integration.command_alkon.masked_secret', {
                lastFour: commandAlkonConnection.apiKeyLastFour,
                interpolation: { escapeValue: false },
              })}
            />
          </Box>

          {testResult === null ? null : (
            <Alert severity={testResult ? 'success' : 'error'} sx={{ mb: 2 }}>
              {t(
                testResult
                  ? 'administration.integration.command_alkon.test_result.accepted'
                  : 'administration.integration.command_alkon.test_result.rejected',
              )}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <SmallLoadingButton
              color="brandV2Yellow"
              loading={isMutating}
              onClick={handleTestClick}
            >
              {t('administration.integration.command_alkon.cta.test_connection')}
            </SmallLoadingButton>
            <Menu
              menuTrigger={
                <OutlinedIconButton aria-label={t('actions.actions')} size="small">
                  <MoreHoriz sx={{ fontSize: '18px' }} />
                </OutlinedIconButton>
              }
              ref={moreActionsMenuRef}
            >
              {renderMoreActionsMenuItem(
                t('administration.integration.command_alkon.cta.replace'),
                () => setIsReplacing(true),
              )}
              {canDelete
                ? renderMoreActionsMenuItem(t('actions.disconnect'), () =>
                    setIsDisconnectDialogOpen(true),
                  )
                : null}
            </Menu>
          </Box>
        </>
      );
    };

    return (
      <Box sx={{ maxWidth: '1000px' }}>
        <Button
          color="secondary"
          component={Link}
          startIcon={<ArrowBackIosNew fontSize="small" />}
          to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
          variant="text"
        >
          {t('administration.integration.title')}
        </Button>
        <SettingsPageHeader
          title={t('administration.integration.command_alkon.name')}
          description={t('administration.integration.command_alkon.description')}
        />

        {renderBody()}

        <ConfirmationDialog
          confirmButtonProps={{
            children: t('actions.disconnect'),
          }}
          isBusy={isMutating}
          isOpen={isDisconnectDialogOpen}
          onClose={() => {
            if (!isMutating) setIsDisconnectDialogOpen(false);
          }}
          onConfirm={handleDisconnectConfirm}
          title={t(
            'administration.integration.command_alkon.disconnect_confirmation_title',
          )}
        >
          <Typography>
            {t('administration.integration.command_alkon.disconnect_confirmation_body')}
          </Typography>
        </ConfirmationDialog>
      </Box>
    );
  },
);

export default CommandAlkonManagement;
